import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronRight, Loader2 } from 'lucide-react';
import AppShell from '@/components/AppShell';
import { gradeAccent } from '@/lib/brand';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GRADE_LABELS = {
  7: 'Kansas History', 8: 'U.S. History', 9: 'World History',
  10: 'Modern World', 11: 'U.S. History', 12: 'Government & Economics',
};

/**
 * Atlas History — reusable unit-picker for tools that operate per-unit.
 * Used by Rubric, Lesson Builder, AI Generate, and similar landing pages.
 *
 * Props:
 *   title, subtitle, icon, buildTargetPath: (unit) => string,
 *   ctaLabel: string
 */
export default function ToolUnitPicker({
  title,
  subtitle,
  icon: Icon,
  buildTargetPath,
  ctaLabel = 'Open',
}) {
  const navigate = useNavigate();
  const [unitsByGrade, setUnitsByGrade] = useState({});
  const [loadedGrades, setLoadedGrades] = useState(new Set());
  const [activeGrade, setActiveGrade] = useState(7);
  const [loadingGrade, setLoadingGrade] = useState(true);

  // Lazy load: only fetch the active grade. Previous version blocked on all 6 grades.
  const loadGrade = useCallback(async (grade) => {
    if (loadedGrades.has(grade)) return;
    setLoadingGrade(true);
    try {
      const res = await axios.get(`${API}/units/grade/${grade}`);
      setUnitsByGrade(prev => ({ ...prev, [grade]: res.data || [] }));
      setLoadedGrades(prev => new Set(prev).add(grade));
    } catch (_e) {
      setUnitsByGrade(prev => ({ ...prev, [grade]: [] }));
      setLoadedGrades(prev => new Set(prev).add(grade));
    } finally {
      setLoadingGrade(false);
    }
  }, [loadedGrades]);

  useEffect(() => { loadGrade(activeGrade); }, [activeGrade, loadGrade]);

  const units = unitsByGrade[activeGrade] || [];

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10">
        <div className="mb-8 flex items-center gap-3">
          {Icon && <Icon className="h-6 w-6 text-[#D4AF37]" />}
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">AI Tools</p>
            <h1 className="font-serif-display text-3xl text-[#14213D] tracking-tight">{title}</h1>
            {subtitle && (
              <p className="text-sm text-[#5A6577] mt-1 max-w-2xl">{subtitle}</p>
            )}
          </div>
        </div>

        {/* Grade tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {[7, 8, 9, 10, 11, 12].map((g) => (
            <button
              key={g}
              onClick={() => setActiveGrade(g)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeGrade === g ? 'bg-[#14213D] text-[#FDFBF7]' : 'bg-white text-[#2D3748] hover:bg-[#F9F8F4] border border-[#EBE7E0]'}`}
              data-testid={`grade-tab-${g}`}
            >
              Grade {g}: {GRADE_LABELS[g]}
            </button>
          ))}
        </div>

        {loadingGrade && !loadedGrades.has(activeGrade) ? (
          <div className="flex flex-col items-center justify-center py-20 text-[#5A6577]">
            <Loader2 className="h-6 w-6 animate-spin mb-3" />
            <p className="text-sm">Loading units…</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {units.map((unit, i) => {
              const accent = gradeAccent(unit.grade_level);
              return (
                <article
                  key={unit.id}
                  onClick={() => navigate(buildTargetPath(unit))}
                  className="group cursor-pointer bg-white border border-[#EBE7E0] rounded-md p-5 hover:-translate-y-1 hover:shadow-md hover:border-[#14213D]/20 transition-all relative overflow-hidden"
                  data-testid={`tool-unit-card-${i}`}
                >
                  <div className="absolute top-0 left-0 w-full h-0.5 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: accent.hex }} />
                  <div className="absolute top-0 left-0 w-1 h-full" style={{ background: accent.hex, opacity: 0.7 }} />
                  <div className="flex items-center justify-between mb-2 pl-1.5">
                    <span className="text-[11px] font-mono tracking-widest uppercase" style={{ color: accent.hex }}>
                      Unit {unit.unit_number}
                    </span>
                    {Icon && <Icon className="h-3.5 w-3.5 text-[#D4AF37]" />}
                  </div>
                  <h3 className="font-serif-display text-[#14213D] text-base mb-2 leading-snug pl-1.5 line-clamp-2">{unit.title}</h3>
                  <p className="text-xs text-[#5A6577] line-clamp-2 pl-1.5 mb-3 leading-relaxed">{unit.description}</p>
                  <div className="flex items-center text-xs text-[#14213D] font-medium mt-2 pl-1.5 group-hover:text-[#D4AF37] transition-colors">
                    <span>{ctaLabel}</span>
                    <ChevronRight className="h-3 w-3 ml-1" />
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
